<template>
    <v-row v-for="(partition, index) in rows" :key="rowKeys[index] ?? index">
        <v-col cols="auto"> {{ index + 1 }}. </v-col>
        <v-col cols="12" sm="5">
            <ValidatableTextField v-model:form="form" :field="`${arrayKey}.${index}.partition_area_ha`" type="number" :min="0" label="Partition Area (ha)" />
        </v-col>
        <v-col cols="12" sm="5">
            <Autocomplete v-model:form="form" :selected-items="partition.landuse_type_id" :items="landuseTypes" :loading="fetchingLanduseTypes" label="Vegetation Type" :field-name="`${arrayKey}.${index}.landuse_type_id`" :chips="false" @update:selected-items="updateLanduseType(index, String($event ?? ''))" />
        </v-col>
        <v-col cols="12" sm="1">
            <v-btn icon="mdi-delete" size="small" variant="text" @click="removePartition(index)" />
        </v-col>
    </v-row>
    <v-row>
        <v-col>
            <v-btn prepend-icon="mdi-plus" variant="text" @click="addPartition">Add Partition</v-btn>
        </v-col>
    </v-row>
</template>

<script setup lang="ts">
import type { ILandUseType } from '@app/land-hold/land-use-type/models/LandUseType';
import type { PrecognitionForm, PrecognitionFormData } from '@shared/types/Form';

export interface PartitionRow {
    partition_area_ha: number | string;
    landuse_type_id: string;
}

const props = defineProps<{
    landuseTypes: ILandUseType[];
    fetchingLanduseTypes: boolean;
    arrayKey: string;
}>();

const form = defineModel<PrecognitionForm<PrecognitionFormData>>('form', {
    required: true,
});

const rows = computed<PartitionRow[]>(() => {
    const value = (form.value as Record<string, unknown>)[props.arrayKey];
    return Array.isArray(value) ? (value as PartitionRow[]) : [];
});

const rowKeys = ref<string[]>(rows.value.map(() => crypto.randomUUID()));

watch(
    () => rows.value.length,
    (length) => {
        while (rowKeys.value.length < length) rowKeys.value.push(crypto.randomUUID());
        while (rowKeys.value.length > length) rowKeys.value.pop();
    },
);

function getMutableRows(): PartitionRow[] | null {
    const value = (form.value as Record<string, unknown>)[props.arrayKey];
    return Array.isArray(value) ? (value as PartitionRow[]) : null;
}

function updateLanduseType(index: number, value: string) {
    const target = getMutableRows();
    if (target?.[index]) {
        target[index].landuse_type_id = value;
    }
}

function addPartition() {
    const target = getMutableRows();
    if (target) {
        target.push({ partition_area_ha: 0, landuse_type_id: '' });
    } else {
        (form.value as Record<string, unknown>)[props.arrayKey] = [{ partition_area_ha: 0, landuse_type_id: '' }];
    }
    rowKeys.value.push(crypto.randomUUID());
}

function removePartition(index: number) {
    const target = getMutableRows();
    if (target) {
        target.splice(index, 1);
    }
    rowKeys.value.splice(index, 1);
}
</script>
